<template>
  <div class="login-page">
    <!-- Background Pattern -->
    <div class="login-page__background">
      <div class="login-page__pattern"></div>
    </div>

    <!-- Login Container -->
    <div class="login-page__container">
      <!-- Left Side - Branding (Desktop Only) -->
      <div class="login-page__branding">
        <div class="login-page__brand-content">
          <div class="login-page__logo">
            <img
              src="/idws_logo.png"
              alt="IDWS Logo"
              class="login-page__logo-image"
            />
          </div>

          <div class="login-page__brand-text">
            <h1 class="login-page__title">Welcome to IDWS</h1>
            <p class="login-page__subtitle">
              International Digital Workplace Summit
            </p>
            <p class="login-page__description">
              Join the future of digital workplace transformation. Connect,
              learn, and innovate with industry leaders from around the world.
            </p>
          </div>

          <div class="login-page__features">
            <div class="login-page__feature">
              <div class="login-page__feature-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m0 0a4 4 0 017 0M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <div>
                <h3>Connect with Experts</h3>
                <p>Network with 1000+ industry professionals</p>
              </div>
            </div>

            <div class="login-page__feature">
              <div class="login-page__feature-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h3>Exclusive Events</h3>
                <p>Access to premium workshops and sessions</p>
              </div>
            </div>

            <div class="login-page__feature">
              <div class="login-page__feature-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <div>
                <h3>Career Growth</h3>
                <p>Advance your digital workplace expertise</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Login Form -->
      <div class="login-page__form-container">
        <div class="login-page__form-content">
          <!-- Mobile Logo (Mobile Only) -->
          <div class="login-page__mobile-logo">
            <img
              src="/idws_logo.png"
              alt="IDWS Logo"
              class="login-page__mobile-logo-image"
            />
          </div>

          <!-- Header -->
          <div class="login-page__header">
            <h2 class="login-page__form-title">Sign In</h2>
            <p class="login-page__form-subtitle">
              Welcome back! Please sign in to your account.
            </p>
          </div>

          <!-- Login Form -->
          <form @submit.prevent="handleLogin" class="login-page__form">
            <!-- Email Field -->
            <div class="login-page__field">
              <BaseInput
                v-model="loginForm.email"
                type="email"
                label="Email Address"
                placeholder="Enter your email"
                required
                :error="!!errors.email"
                :error-message="errors.email"
                autocomplete="email"
              >
                <template #prefix>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                </template>
              </BaseInput>
            </div>

            <!-- Password Field -->
            <div class="login-page__field">
              <BaseInput
                v-model="loginForm.password"
                :type="showPassword ? 'text' : 'password'"
                label="Password"
                placeholder="Enter your password"
                required
                :error="!!errors.password"
                :error-message="errors.password"
                autocomplete="current-password"
              >
                <template #prefix>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                    <circle cx="12" cy="16" r="1" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </template>
                <template #suffix>
                  <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="login-page__password-toggle"
                    aria-label="Toggle password visibility"
                  >
                    <svg
                      v-if="showPassword"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                      />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                    <svg
                      v-else
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </template>
              </BaseInput>
            </div>

            <!-- Login Error Alert -->
            <div v-if="loginError" class="login-page__error-alert">
              <div class="login-page__error-content">
                <svg class="login-page__error-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                <span class="login-page__error-message">{{ loginError }}</span>
              </div>
            </div>

            <!-- Remember Me & Forgot Password -->
            <div class="login-page__options">
              <label class="login-page__checkbox">
                <input
                  v-model="loginForm.rememberMe"
                  type="checkbox"
                  class="login-page__checkbox-input"
                />
                <span class="login-page__checkbox-label">Remember me</span>
              </label>

              <button
                type="button"
                @click="handleForgotPassword"
                class="login-page__forgot-password"
              >
                Forgot password?
              </button>
            </div>

            <!-- Submit Button -->
            <BaseButton
              type="submit"
              variant="primary"
              size="lg"
              :loading="isLoading"
              class="login-page__submit-btn"
            >
              <template #icon v-if="!isLoading">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
              </template>
              {{ isLoading ? "Signing In..." : "Sign In" }}
            </BaseButton>
          </form>

          <!-- Divider -->
          <!-- <div class="login-page__divider">
            <span>Or continue with</span>
          </div> -->

          <!-- Social Login -->
          <!-- <div class="login-page__social">
            <button
              @click="handleSocialLogin('google')"
              class="login-page__social-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </button>

            <button
              @click="handleSocialLogin('microsoft')"
              class="login-page__social-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path fill="#F25022" d="M1 1h10v10H1z" />
                <path fill="#00A4EF" d="M13 1h10v10H13z" />
                <path fill="#7FBA00" d="M1 13h10v10H1z" />
                <path fill="#FFB900" d="M13 13h10v10H13z" />
              </svg>
              Microsoft
            </button>
          </div> -->

          <!-- Sign Up Link -->
          <!-- <div class="login-page__signup">
            <p>
              Don't have an account?
              <button @click="handleSignUp" class="login-page__signup-link">
                Sign up here
              </button>
            </p>
          </div> -->
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { defineComponent, ref, reactive } from "vue";
import { useRouter } from "vue-router";
import useNotifications from "@/composables/useNotifications";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import { AuthAPI } from "@/api/auth.js";

export default defineComponent({
  name: "HomeView",
  components: {
    BaseInput,
    BaseButton,
  },
  setup() {
    const { success, error, info } = useNotifications();
    const router = useRouter();

    const isLoading = ref(false);
    const showPassword = ref(false);

    const loginForm = reactive({
      email: "",
      password: "",
      rememberMe: false,
    });

    const errors = reactive({
      email: "",
      password: "",
    });

    const loginError = ref("");

    // Form validation
    const validateForm = () => {
      let isValid = true;

      // Reset errors
      errors.email = "";
      errors.password = "";

      // Email validation
      if (!loginForm.email) {
        errors.email = "Email is required";
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(loginForm.email)) {
        errors.email = "Please enter a valid email address";
        isValid = false;
      }

      // Password validation
      if (!loginForm.password) {
        errors.password = "Password is required";
        isValid = false;
      } else if (loginForm.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
        isValid = false;
      }

      return isValid;
    };

    // Handle login submission
    const handleLogin = async () => {
      loginError.value = "";

      if (!validateForm()) {
        return;
      }

      isLoading.value = true;

      try {
        // Call actual API
        const response = await AuthAPI.login({
          email: loginForm.email,
          password: loginForm.password,
          device_name: "Web Browser"
        });

        if (response.success) {
          success("Welcome back! Login successful");

          // Store remember me preference
          if (loginForm.rememberMe) {
            localStorage.setItem("idws_remember_email", loginForm.email);
          }

          // Redirect to dashboard
          await router.push('/dashboard');
        } else {
          loginError.value = response.message || "Invalid email or password";
        }
      } catch (err) {
        console.error("Login error:", err);
        loginError.value = "Invalid email or password. Please try again.";
      } finally {
        isLoading.value = false;
      }
    };

    // Handle forgot password
    const handleForgotPassword = () => {
      info("Password reset functionality coming soon!");
    };

    // Handle social login
    const handleSocialLogin = (provider) => {
      info(`${provider} login integration coming soon!`);
    };

    // Handle sign up
    const handleSignUp = () => {
      info("Registration page coming soon!");
    };

    // Load remembered email on component mount
    const rememberEmail = localStorage.getItem("idws_remember_email");
    if (rememberEmail) {
      loginForm.email = rememberEmail;
      loginForm.rememberMe = true;
    }

    return {
      isLoading,
      showPassword,
      loginForm,
      errors,
      loginError,
      handleLogin,
      handleForgotPassword,
      handleSocialLogin,
      handleSignUp,
    };
  },
});
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  position: relative;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-light) 100%
  );
}

/* Background Pattern */
.login-page__background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.login-page__pattern {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background-image: radial-gradient(
      circle at 25% 25%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 75% 75%,
      rgba(122, 201, 67, 0.1) 0%,
      transparent 50%
    );
  animation: float 20s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  50% {
    transform: translate(-50%, -50%) rotate(180deg);
  }
}

/* Main Container */
.login-page__container {
  display: flex;
  width: 100%;
  max-width: 1300px;
  margin: var(--space-4) auto;
  background-color: var(--color-white);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-2xl);
  overflow: hidden;
  position: relative;
  z-index: 2;
}

/* Left Side - Branding */
.login-page__branding {
  flex: 0 0 45%;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  color: var(--color-white);
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.login-page__branding::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.03)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
}

.login-page__brand-content {
  position: relative;
  z-index: 1;
}

.login-page__logo {
  margin-bottom: var(--space-3);
}

.login-page__logo-image {
  height: 6rem;
  filter: brightness(0) invert(1);
}

.login-page__title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-2);
  background: linear-gradient(
    45deg,
    var(--color-white),
    var(--color-highlight)
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.login-page__subtitle {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-3);
  opacity: 0.9;
}

.login-page__description {
  font-size: var(--font-size-base);
  line-height: 1.5;
  margin-bottom: var(--space-6);
  opacity: 0.8;
}

.login-page__features {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.login-page__feature {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.login-page__feature-icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-highlight);
}

.login-page__feature h3 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--space-1);
}

.login-page__feature p {
  opacity: 0.8;
  margin: 0;
}

/* Right Side - Form */
.login-page__form-container {
  flex: 0 0 55%;
  padding: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-page__form-content {
  width: 100%;
  max-width: 400px;
}

.login-page__header {
  text-align: center;
  margin-bottom: var(--space-4);
}

.login-page__form-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-900);
  margin-bottom: var(--space-2);
}

.login-page__form-subtitle {
  color: var(--color-gray-600);
  font-size: var(--font-size-base);
  margin: 0;
}

/* Form */
.login-page__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
}

.login-page__field {
  position: relative;
}

.login-page__password-toggle {
  background: none;
  border: none;
  color: var(--color-gray-400);
  cursor: pointer;
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  transition: color var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-page__password-toggle:hover {
  color: var(--color-gray-600);
}

.login-page__password-toggle:focus {
  outline: none;
  color: var(--color-primary);
}

.login-page__options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: var(--space-2) 0;
}

.login-page__checkbox {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
}

.login-page__checkbox-input {
  width: 1rem;
  height: 1rem;
  accent-color: var(--color-primary);
}

.login-page__checkbox-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-700);
}

.login-page__forgot-password {
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  text-decoration: none;
  transition: color var(--transition-fast);
}

.login-page__forgot-password:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}

.login-page__submit-btn {
  width: 100%;
  justify-content: center;
}

/* Divider */
.login-page__divider {
  position: relative;
  text-align: center;
  margin: var(--space-8) 0;
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

.login-page__divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background-color: var(--color-gray-200);
  z-index: 1;
}

.login-page__divider span {
  background-color: var(--color-white);
  padding: 0 var(--space-4);
  position: relative;
  z-index: 2;
}

/* Social Login */
.login-page__social {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-8);
}

.login-page__social-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  background-color: var(--color-white);
  color: var(--color-gray-700);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.login-page__social-btn:hover {
  border-color: var(--color-gray-400);
  background-color: var(--color-gray-50);
}

/* Sign Up */
.login-page__signup {
  text-align: center;
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
}

.login-page__signup-link {
  background: none;
  border: none;
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  text-decoration: none;
  transition: color var(--transition-fast);
}

.login-page__signup-link:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}

/* Mobile Logo (Hidden on Desktop) */
.login-page__mobile-logo {
  display: none;
  text-align: center;
  margin-bottom: var(--space-6);
}

.login-page__mobile-logo-image {
  height: 4rem;
  max-width: 100%;
  object-fit: contain;
}

/* Responsive Design */
@media (max-width: 1023px) {
  .login-page__container {
    flex-direction: column;
    margin: var(--space-4);
  }

  .login-page__branding {
    display: none;
  }

  .login-page__mobile-logo {
    display: block;
  }

  .login-page__form-container {
    flex: 1;
    padding: var(--space-6);
    justify-content: flex-start;
    padding-top: var(--space-8);
  }

  .login-page__form-content {
    max-width: 100%;
  }
}

@media (max-width: 767px) {
  .login-page {
    background: var(--color-white);
  }

  .login-page__background {
    display: none;
  }

  .login-page__container {
    margin: 0;
    border-radius: 0;
    min-height: 100vh;
    background: var(--color-white);
    box-shadow: none;
  }

  .login-page__form-container {
    padding: var(--space-4);
    justify-content: center;
    padding-top: var(--space-8);
  }

  .login-page__mobile-logo {
    margin-bottom: var(--space-8);
  }

  .login-page__mobile-logo-image {
    height: 5rem;
  }

  .login-page__social {
    flex-direction: column;
  }

  .login-page__options {
    flex-direction: column;
    gap: var(--space-4);
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .login-page__form-container {
    padding: var(--space-3);
    padding-top: var(--space-6);
  }

  .login-page__mobile-logo {
    margin-bottom: var(--space-6);
  }

  .login-page__mobile-logo-image {
    height: 4rem;
  }

  .login-page__form-title {
    font-size: var(--font-size-xl);
  }

  .login-page__header {
    margin-bottom: var(--space-3);
  }
}

/* Login Error Alert */
.login-page__error-alert {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--radius-base);
  padding: var(--space-3);
  margin-top: var(--space-2);
}

.login-page__error-content {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.login-page__error-icon {
  width: 1rem;
  height: 1rem;
  color: #dc2626;
  flex-shrink: 0;
}

.login-page__error-message {
  color: #dc2626;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}
</style>
